import { useEffect, useState, useMemo } from 'react'
import { useInvoices } from '@/hooks/useInvoices'
import {TrendingUp, DollarSign, FileText, AlertCircle, Users, Calendar, PieChart, BarChart3, Receipt} from 'lucide-react'

interface MetricCard {
  title: string
  value: string
  change?: string
  trend?: 'up' | 'down'
  icon: any
  color: string
}

export default function AnalyticsDashboard() {
  const { invoices, loading, fetchInvoices } = useInvoices()
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d')
  const [selectedCurrency, setSelectedCurrency] = useState<string>('all')

  useEffect(() => {
    fetchInvoices({ sort: { createdAt: -1 } })
  }, [])

  // Filter invoices by date range
  const filteredInvoices = useMemo(() => {
    if (dateRange === 'all') return invoices
    
    const now = new Date()
    const daysMap = { '7d': 7, '30d': 30, '90d': 90 }
    const days = daysMap[dateRange]
    
    return invoices.filter(inv => {
      const invDate = new Date(inv.createdAt)
      const daysDiff = Math.floor((now.getTime() - invDate.getTime()) / (1000 * 60 * 60 * 24))
      return daysDiff <= days
    })
  }, [invoices, dateRange])

  // Filter by currency
  const currencyFilteredInvoices = useMemo(() => {
    if (selectedCurrency === 'all') return filteredInvoices
    return filteredInvoices.filter(inv => inv.currency === selectedCurrency)
  }, [filteredInvoices, selectedCurrency])

  // Calculate metrics including tax
  const metrics = useMemo(() => {
    const totalRevenue = currencyFilteredInvoices
      .filter(inv => inv.status === 'paid')
      .reduce((sum, inv) => sum + inv.totalAmount, 0)
    
    const pendingRevenue = currencyFilteredInvoices
      .filter(inv => inv.status === 'pending_payment')
      .reduce((sum, inv) => sum + inv.totalAmount, 0)
    
    const outstandingRevenue = currencyFilteredInvoices
      .filter(inv => inv.status === 'sent')
      .reduce((sum, inv) => sum + inv.totalAmount, 0)
    
    const totalTaxCollected = currencyFilteredInvoices
      .filter(inv => inv.status === 'paid')
      .reduce((sum, inv) => sum + (inv.taxAmount || 0), 0)
    
    const pendingTax = currencyFilteredInvoices
      .filter(inv => inv.status !== 'paid' && inv.status !== 'draft')
      .reduce((sum, inv) => sum + (inv.taxAmount || 0), 0)
    
    const totalInvoices = currencyFilteredInvoices.length
    const paidCount = currencyFilteredInvoices.filter(inv => inv.status === 'paid').length
    const pendingCount = currencyFilteredInvoices.filter(inv => inv.status === 'pending_payment').length
    const sentCount = currencyFilteredInvoices.filter(inv => inv.status === 'sent').length
    const draftCount = currencyFilteredInvoices.filter(inv => inv.status === 'draft').length
    
    const uniqueClients = new Set(currencyFilteredInvoices.map(inv => inv.clientName)).size

    return {
      totalRevenue,
      pendingRevenue,
      outstandingRevenue,
      totalTaxCollected,
      pendingTax,
      totalInvoices,
      paidCount,
      pendingCount,
      sentCount,
      draftCount,
      uniqueClients
    }
  }, [currencyFilteredInvoices])

  // Currency breakdown
  const currencyBreakdown = useMemo(() => {
    const breakdown: Record<string, { total: number; count: number }> = {}
    
    filteredInvoices.forEach(inv => {
      if (!breakdown[inv.currency]) {
        breakdown[inv.currency] = { total: 0, count: 0 }
      }
      if (inv.status === 'paid') {
        breakdown[inv.currency].total += inv.totalAmount
      }
      breakdown[inv.currency].count++
    })
    
    return Object.entries(breakdown).map(([currency, data]) => ({
      currency,
      total: data.total,
      count: data.count
    }))
  }, [filteredInvoices])

  // Tax breakdown by rate
  const taxBreakdown = useMemo(() => {
    const breakdown: Record<number, { collected: number; pending: number; count: number }> = {}
    
    currencyFilteredInvoices.forEach(inv => {
      const rate = inv.taxRate || 0
      if (!breakdown[rate]) {
        breakdown[rate] = { collected: 0, pending: 0, count: 0 }
      }
      if (inv.status === 'paid') {
        breakdown[rate].collected += (inv.taxAmount || 0)
      } else if (inv.status !== 'draft') {
        breakdown[rate].pending += (inv.taxAmount || 0)
      }
      breakdown[rate].count++
    })
    
    return Object.entries(breakdown)
      .map(([rate, data]) => ({ rate: parseFloat(rate), ...data }))
      .sort((a, b) => b.collected - a.collected)
  }, [currencyFilteredInvoices])

  // Top clients
  const topClients = useMemo(() => {
    const clientMap: Record<string, { total: number; count: number }> = {}
    
    currencyFilteredInvoices.forEach(inv => {
      if (!clientMap[inv.clientName]) {
        clientMap[inv.clientName] = { total: 0, count: 0 }
      }
      if (inv.status === 'paid') {
        clientMap[inv.clientName].total += inv.totalAmount
      }
      clientMap[inv.clientName].count++
    })
    
    return Object.entries(clientMap)
      .map(([name, data]) => ({ name, total: data.total, count: data.count }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5)
  }, [currencyFilteredInvoices])

  // Monthly revenue trend
  const monthlyTrend = useMemo(() => {
    const months: Record<string, number> = {}
    
    currencyFilteredInvoices
      .filter(inv => inv.status === 'paid')
      .forEach(inv => {
        const date = new Date(inv.invoiceDate)
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
        months[monthKey] = (months[monthKey] || 0) + inv.totalAmount
      })
    
    return Object.entries(months)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([month, revenue]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        revenue
      }))
  }, [currencyFilteredInvoices])

  const getCurrencySymbol = (currency: string) => {
    const symbols: Record<string, string> = {
      USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$'
    }
    return symbols[currency] || '$'
  }

  const metricCards: MetricCard[] = [
    {
      title: 'Total Revenue',
      value: `${getCurrencySymbol(selectedCurrency === 'all' ? 'USD' : selectedCurrency)}${metrics.totalRevenue.toFixed(2)}`,
      icon: DollarSign,
      color: 'bg-green-500',
      change: '+12.5%',
      trend: 'up'
    },
    {
      title: 'Pending Payments',
      value: `${getCurrencySymbol(selectedCurrency === 'all' ? 'USD' : selectedCurrency)}${metrics.pendingRevenue.toFixed(2)}`,
      icon: AlertCircle,
      color: 'bg-yellow-500',
      change: `${metrics.pendingCount} invoices`
    },
    {
      title: 'Outstanding',
      value: `${getCurrencySymbol(selectedCurrency === 'all' ? 'USD' : selectedCurrency)}${metrics.outstandingRevenue.toFixed(2)}`,
      icon: TrendingUp,
      color: 'bg-orange-500',
      change: `${metrics.sentCount} invoices`
    },
    {
      title: 'Total Invoices',
      value: metrics.totalInvoices.toString(),
      icon: FileText,
      color: 'bg-blue-500',
      change: `${metrics.paidCount} paid`
    },
    {
      title: 'Unique Clients',
      value: metrics.uniqueClients.toString(),
      icon: Users,
      color: 'bg-purple-500',
      change: 'Active clients'
    },
    {
      title: 'Draft Invoices',
      value: metrics.draftCount.toString(),
      icon: Calendar,
      color: 'bg-gray-500',
      change: 'In progress'
    }
  ]

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 py-12 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B5394] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Analytics Dashboard</h1>
          <p className="text-gray-600">Track your invoice performance and revenue insights</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as any)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent"
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="all">All Time</option>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
              <select
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent"
              >
                <option value="all">All Currencies</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="CAD">CAD (C$)</option>
                <option value="AUD">AUD (A$)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {metricCards.map((card, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 mb-2">{card.title}</p>
                  <p className="text-3xl font-bold text-gray-900 mb-2">{card.value}</p>
                  {card.change && (
                    <p className={`text-sm ${card.trend === 'up' ? 'text-green-600' : 'text-gray-600'}`}>
                      {card.change}
                    </p>
                  )}
                </div>
                <div className={`${card.color} p-3 rounded-lg`}>
                  <card.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Status Distribution */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <PieChart className="w-5 h-5 text-[#0B5394]" />
              <h2 className="text-xl font-bold text-gray-900">Invoice Status Distribution</h2>
            </div>
            <div className="space-y-4">
              {[
                { label: 'Paid', count: metrics.paidCount, color: 'bg-green-500', percentage: (metrics.paidCount / metrics.totalInvoices * 100) || 0 },
                { label: 'Pending Payment', count: metrics.pendingCount, color: 'bg-yellow-500', percentage: (metrics.pendingCount / metrics.totalInvoices * 100) || 0 },
                { label: 'Sent', count: metrics.sentCount, color: 'bg-blue-500', percentage: (metrics.sentCount / metrics.totalInvoices * 100) || 0 },
                { label: 'Draft', count: metrics.draftCount, color: 'bg-gray-500', percentage: (metrics.draftCount / metrics.totalInvoices * 100) || 0 }
              ].map((status) => (
                <div key={status.label}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">{status.label}</span>
                    <span className="text-sm text-gray-600">{status.count} ({status.percentage.toFixed(1)}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`${status.color} h-3 rounded-full transition-all duration-500`}
                      style={{ width: `${status.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Currency Breakdown */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <DollarSign className="w-5 h-5 text-[#0B5394]" />
              <h2 className="text-xl font-bold text-gray-900">Revenue by Currency</h2>
            </div>
            <div className="space-y-4">
              {currencyBreakdown.map((item) => (
                <div key={item.currency} className="flex justify-between items-center p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
                  <div>
                    <p className="font-semibold text-gray-900">{item.currency}</p>
                    <p className="text-sm text-gray-600">{item.count} invoices</p>
                  </div>
                  <p className="text-2xl font-bold text-[#047857]">
                    {getCurrencySymbol(item.currency)}{item.total.toFixed(2)}
                  </p>
                </div>
              ))}
              {currencyBreakdown.length === 0 && (
                <p className="text-gray-500 text-center py-8">No paid invoices yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Monthly Revenue Trend */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="w-5 h-5 text-[#0B5394]" />
            <h2 className="text-xl font-bold text-gray-900">Revenue Trend (Last 6 Months)</h2>
          </div>
          {monthlyTrend.length > 0 ? (
            <div className="space-y-4">
              {monthlyTrend.map((item) => {
                const maxRevenue = Math.max(...monthlyTrend.map(t => t.revenue))
                const percentage = (item.revenue / maxRevenue) * 100
                
                return (
                  <div key={item.month}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">{item.month}</span>
                      <span className="text-sm font-bold text-[#047857]">
                        {getCurrencySymbol(selectedCurrency === 'all' ? 'USD' : selectedCurrency)}{item.revenue.toFixed(2)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-gradient-to-r from-[#0B5394] to-[#047857] h-4 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No revenue data available</p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Tax Collection Summary */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <Receipt className="w-5 h-5 text-[#0B5394]" />
              <h2 className="text-xl font-bold text-gray-900">Tax Collection Summary</h2>
            </div>
            <div className="space-y-6">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-gray-600 mb-1">Tax Collected (Paid Invoices)</p>
                <p className="text-3xl font-bold text-green-600">
                  {getCurrencySymbol(selectedCurrency === 'all' ? 'USD' : selectedCurrency)}{metrics.totalTaxCollected.toFixed(2)}
                </p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-gray-600 mb-1">Pending Tax (Outstanding Invoices)</p>
                <p className="text-3xl font-bold text-yellow-600">
                  {getCurrencySymbol(selectedCurrency === 'all' ? 'USD' : selectedCurrency)}{metrics.pendingTax.toFixed(2)}
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-gray-600 mb-1">Total Tax Liability</p>
                <p className="text-3xl font-bold text-blue-600">
                  {getCurrencySymbol(selectedCurrency === 'all' ? 'USD' : selectedCurrency)}{(metrics.totalTaxCollected + metrics.pendingTax).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Tax Breakdown by Rate */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <PieChart className="w-5 h-5 text-[#0B5394]" />
              <h2 className="text-xl font-bold text-gray-900">Tax Breakdown by Rate</h2>
            </div>
            {taxBreakdown.length > 0 ? (
              <div className="space-y-4">
                {taxBreakdown.map((item) => (
                  <div key={item.rate} className="p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-gray-900">{item.rate}% Tax Rate</span>
                      <span className="text-sm text-gray-600">{item.count} invoices</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Collected:</span>
                        <span className="font-semibold text-green-600">
                          {getCurrencySymbol(selectedCurrency === 'all' ? 'USD' : selectedCurrency)}{item.collected.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Pending:</span>
                        <span className="font-semibold text-yellow-600">
                          {getCurrencySymbol(selectedCurrency === 'all' ? 'USD' : selectedCurrency)}{item.pending.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No tax data available</p>
            )}
          </div>
        </div>

        {/* Top Clients */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-2 mb-6">
            <Users className="w-5 h-5 text-[#0B5394]" />
            <h2 className="text-xl font-bold text-gray-900">Top 5 Clients by Revenue</h2>
          </div>
          {topClients.length > 0 ? (
            <div className="space-y-4">
              {topClients.map((client, idx) => (
                <div key={client.name} className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#0B5394] text-white flex items-center justify-center font-bold">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{client.name}</p>
                    <p className="text-sm text-gray-600">{client.count} invoice{client.count !== 1 ? 's' : ''}</p>
                  </div>
                  <p className="text-xl font-bold text-[#047857]">
                    {getCurrencySymbol(selectedCurrency === 'all' ? 'USD' : selectedCurrency)}{client.total.toFixed(2)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No client data available</p>
          )}
        </div>
      </div>
    </div>
  )
}
